import {
  useState, useMemo 
} from 'react';
import {
  X, Pencil 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/classnames';
import type { Recipe } from '../../types';
import { RECIPE_TYPE_LABELS } from '../../types';
import {
  DetailsTab, IngredientsTab, InstructionsTab, UsedInTab, typeColors 
} from './detail-tabs';

interface RecipeDetailModalProps {
  readonly recipe: Recipe | null;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly allRecipes?: Recipe[];
  readonly onNavigateToRecipe?: (recipe: Recipe) => void;
  readonly onEdit?: (recipe: Recipe) => void;
}

type TabId = 'details' | 'ingredients' | 'instructions' | 'used-in';

export function RecipeDetailModal({
  recipe,
  isOpen,
  onClose,
  allRecipes = [],
  onNavigateToRecipe,
  onEdit,
}: RecipeDetailModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('details');

  const subRecipes = useMemo(
    () => allRecipes.filter((r) => recipe?.subRecipeIds?.includes(r.recipeId)),
    [allRecipes, recipe?.subRecipeIds]
  );

  const usedInRecipes = useMemo(
    () => allRecipes.filter((r) => r.subRecipeIds?.includes(recipe?.recipeId ?? '')),
    [allRecipes, recipe?.recipeId]
  );

  const isSubRecipeOrComponent = recipe?.type === 'sub-recipe' || recipe?.type === 'component';

  if (!isOpen || !recipe) return null;

  const handleNavigate = (targetRecipe: Recipe) => {
    if (onNavigateToRecipe) {
      onNavigateToRecipe(targetRecipe);
    }
    setActiveTab('details');
  };

  const tabs: {
    id: TabId;
    label: string;
    show: boolean 
  }[] = [
    {
      id: 'details',
      label: 'Details',
      show: true 
    },
    {
      id: 'ingredients',
      label: `Ingredients (${recipe.ingredients.length})`,
      show: true 
    },
    {
      id: 'instructions',
      label: `Instructions (${recipe.instructions?.length ?? 0})`,
      show: (recipe.instructions?.length ?? 0) > 0 
    },
    {
      id: 'used-in',
      label: `Used In (${usedInRecipes.length})`,
      show: isSubRecipeOrComponent 
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-3xl h-[600px] max-h-[90vh] flex flex-col rounded-lg bg-background shadow-xl">
        {/* Header */}
        <div className="flex-shrink-0 border-b px-6 py-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold">{recipe.name}</h2>
                <span className={cn('rounded-full px-2 py-1 text-xs font-medium', typeColors[recipe.type])}>
                  {RECIPE_TYPE_LABELS[recipe.type]}
                </span>
              </div>
              {recipe.cuisine && (
                <p className="text-sm text-muted-foreground mt-1">{recipe.cuisine} Cuisine</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {onEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(recipe)}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 hover:bg-muted transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 border-b px-6">
          <nav className="flex gap-4">
            {tabs.filter((t) => t.show).map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'border-b-2 px-1 py-3 text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'details' && (
            <DetailsTab recipe={recipe} subRecipes={subRecipes} onNavigate={handleNavigate} />
          )}
          {activeTab === 'ingredients' && <IngredientsTab recipe={recipe} />}
          {activeTab === 'instructions' && recipe.instructions && (
            <InstructionsTab instructions={recipe.instructions} />
          )}
          {activeTab === 'used-in' && (
            <UsedInTab recipe={recipe} usedInRecipes={usedInRecipes} onNavigate={handleNavigate} />
          )}
        </div>
      </div>
    </div>
  );
}
